import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Edit } from 'lucide-react';
import { showToast } from '../utils/toast';
import FormField from './FormField';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { validateName, validateEmail, validatePhone, validateRole } from '../utils/formValidation';
import { validatePhoneRealTime } from '../utils/phoneValidation';

const EditUserModal = ({ open, onClose, user, onSubmit }) => {
  const { updateUser, users } = useData();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: ''
  });

  // Validation states
  const [nameValidation, setNameValidation] = useState({ isValid: true, message: '' });
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('in'); // Track selected country for validation
  const [roleValidation, setRoleValidation] = useState({ isValid: true, message: '' });

  // Initialize form data when user prop changes or modal opens
  useEffect(() => {
    if (user && open) {
      // Ensure phone number includes country code for PhoneInput (international format)
      let phoneValue = user.phone_number || '';
      // If phone doesn't start with +, add + prefix
      if (phoneValue && !phoneValue.startsWith('+')) {
        // If it's a 10-digit number, assume Indian (+91)
        const digitsOnly = phoneValue.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
          phoneValue = '+91' + digitsOnly;
        } else if (digitsOnly.length > 10) {
          // If it already has country code without +, add +
          phoneValue = '+' + digitsOnly;
        } else {
          // Keep as is if already has + or format is unknown
          phoneValue = '+' + digitsOnly;
        }
      }

      setFormData({
        name: user.full_name || user.name || '',
        email: user.email || '',
        role: user.role || '',
        phone: phoneValue
      });

      // Reset validation states
      setNameValidation({ isValid: true, message: '' });
      setEmailValidation({ isValid: true, message: '' });
      setPhoneValidation({ isValid: true, message: '' });
      setRoleValidation({ isValid: true, message: '' });
    }
  }, [user, open]);

  // Check if user is admin (read-only role)
  const isAdminUser = user?.role === 'admin';

  // Check if user is vendor (read-only role - vendor role cannot be changed)
  const isVendorUser = user?.role === 'vendor';

  // Dynamic roles: Only show admin option if user is already admin
  // For vendors, show vendor role but keep it disabled (like admin)
  const roles = isAdminUser
    ? [
      { value: 'employee', label: 'Employee' },
      { value: 'business_head', label: 'Business Head' },
      { value: 'admin', label: 'Administrator' },

    ]
    : isVendorUser
      ? [
        { value: 'vendor', label: 'Vendor' }
      ]
      : [
        { value: 'employee', label: 'Employee' },
        { value: 'business_head', label: 'Business Head' },

      ];

  // Validation handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, name: value }));
    setNameValidation(validateName(value, 'Full name'));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    setEmailValidation(validateEmail(value));
  };

  const handlePhoneChange = (value, country, e, formattedValue) => {
    // react-phone-input-2 onChange signature: (value, country, e, formattedValue)
    // value: phone number with country code but without + (e.g., "919876543210")
    // country: country code object { name: "India", dialCode: "91", countryCode: "in" }
    // Convert to international format with + prefix
    const internationalFormat = value.startsWith('+') ? value : `+${value}`;
    setFormData(prev => ({ ...prev, phone: internationalFormat }));
    setPhoneTouched(true);

    // Update selected country for validation
    if (country?.countryCode) {
      setSelectedCountry(country.countryCode.toLowerCase());
    }

    // Real-time validation with country-specific feedback
    const countryCode = country?.countryCode?.toUpperCase() || selectedCountry?.toUpperCase() || null;
    const validation = validatePhoneRealTime(internationalFormat, countryCode);
    setPhoneValidation(validation);
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
    setRoleValidation(validateRole(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate all fields
      const nameValidationResult = validateName(formData.name, 'Full name');
      const emailValidationResult = validateEmail(formData.email);
      const phoneValidationResult = validatePhone(formData.phone);
      const roleValidationResult = validateRole(formData.role);

      if (!nameValidationResult.isValid) {
        setNameValidation(nameValidationResult);
        showToast.error(nameValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!emailValidationResult.isValid) {
        setEmailValidation(emailValidationResult);
        showToast.error(emailValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!phoneValidationResult.isValid) {
        setPhoneValidation(phoneValidationResult);
        showToast.error(phoneValidationResult.message);
        setIsLoading(false);
        return;
      }

      if (!roleValidationResult.isValid) {
        setRoleValidation(roleValidationResult);
        showToast.error(roleValidationResult.message);
        setIsLoading(false);
        return;
      }

      // Check if email already exists (excluding current user)
      const existingUser = users.find(u =>
        u.email.toLowerCase() === formData.email.toLowerCase() && u.id !== user.id
      );
      if (existingUser) {
        setEmailValidation({ isValid: false, message: 'Email already exists' });
        showToast.error('User with this email already exists');
        setIsLoading(false);
        return;
      }

      // Prevent current user from changing their own role to non-admin
      if (user.id === currentUser.id && currentUser.role === 'admin' && formData.role !== 'admin') {
        showToast.error('You cannot change your own admin role');
        return;
      }

      // Prevent updating users to admin role (backend restriction)
      if (formData.role === 'admin' && user.role !== 'admin') {
        showToast.error('Cannot promote users to admin role via this interface');
        setIsLoading(false);
        return;
      }

      // Prevent changing vendor role (vendor role should remain vendor)
      if (isVendorUser && formData.role !== 'vendor') {
        showToast.error('Vendor role cannot be changed - only name, email, and phone number can be updated');
        setIsLoading(false);
        return;
      }

      // Ensure phone number is in international format with + prefix
      const formPhone = formData.phone?.startsWith('+')
        ? formData.phone.trim()
        : formData.phone ? `+${formData.phone.trim()}` : user.phone_number;

      // Ensure vendor role remains vendor (extra safeguard)
      const finalRole = isVendorUser ? 'vendor' : formData.role;

      const userData = {
        full_name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        role: finalRole,
        phone_number: formPhone
      };

      // Use the onSubmit prop if provided
      if (onSubmit) {
        await onSubmit(user.id, userData);
      } else {
        // Fallback to DataContext method
        updateUser(user.id, userData);
        showToast.success('User updated successfully!');
      }

      onClose();
    } catch (error) {
      showToast.error('Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    if (user) {
      // Ensure phone number includes country code for PhoneInput (international format)
      let phoneValue = user.phone_number || '';
      if (phoneValue && !phoneValue.startsWith('+')) {
        const digitsOnly = phoneValue.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
          phoneValue = '+91' + digitsOnly;
        } else if (digitsOnly.length > 10) {
          phoneValue = '+' + digitsOnly;
        } else {
          phoneValue = '+' + digitsOnly;
        }
      }

      setFormData({
        name: user.full_name || user.name || '',
        email: user.email || '',
        role: user.role || '',
        phone: phoneValue
      });

      // Reset validation states
      setNameValidation({ isValid: true, message: '' });
      setEmailValidation({ isValid: true, message: '' });
      setPhoneValidation({ isValid: true, message: '' });
      setRoleValidation({ isValid: true, message: '' });
    }
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] h-[95vh] sm:h-auto sm:max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-yellow-600" />
            Edit User
          </DialogTitle>
          <DialogDescription>
            Update user information, role, and permissions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {/* Name */}
            <FormField
              label="Full Name"
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              validation={nameValidation}
              placeholder="Enter full name"
              required
            />

            {/* Email */}
            <FormField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              validation={emailValidation}
              placeholder="user@company.com"
              required
            />

            {/* Phone */}
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                country={'in'}
                value={formData.phone}
                onChange={handlePhoneChange}
                className="w-full"
                inputStyle={{ width: "100%", height: "40px" }}
                required
                preferredCountries={['in', 'us', 'gb', 'ae', 'sa']}
                onlyCountries={[]}  // Allow all countries
              />
              {/* Real-time validation feedback */}
              {phoneTouched && (
                <div className="mt-1 space-y-1">
                  {phoneValidation.isValid ? (
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-green-600 font-medium">
                        {phoneValidation.message}
                      </p>
                    </div>
                  ) : (
                   ""
                  )}
                </div>
              )}
            </div>

            {/* Role */}
            <FormField
              label="Role"
              type="select"
              value={formData.role}
              onChange={handleRoleChange}
              validation={roleValidation}
              placeholder="Select a role"
              required
              options={roles}
              disabled={isAdminUser || isVendorUser}
            />
            {isAdminUser && (
              <p className="text-xs text-gray-500 -mt-2 flex items-center gap-1">
                <span>🔒</span>
                <span>Admin role cannot be changed via UI - system managed only</span>
              </p>
            )}
          </div>

          {/* Role Permissions Info */}
          {formData.role && formData.role !== user.role && (
            <Card className="bg-yellow-50 border-yellow-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-yellow-800">Role Change Warning</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-sm text-yellow-700">
                  <p>Changing the user's role will modify their system permissions and access levels.</p>
                  <p className="mt-1 font-medium">
                    New role: <span className="capitalize">{formData.role.replace('_', ' ')}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 sm:flex-initial"
              data-testid="cancel-edit-user-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !phoneValidation.isValid || !nameValidation.isValid || !emailValidation.isValid || !roleValidation.isValid}
              className="btn-gold flex-1 sm:flex-initial disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="update-user-submit-btn"
            >
              {isLoading ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;